<template>
  <div class="p-4 md:p-6">
    <!-- Header -->
    <div class="flex items-start justify-between gap-3 mb-6 md:mb-8">
      <div class="min-w-0">
        <h1 class="text-xl md:text-2xl font-bold text-gray-900">סקירת פעילות</h1>
        <p class="text-xs md:text-sm text-gray-400 mt-1">סקירה כללית של כל העסקים בפלטפורמה</p>
      </div>
      <div class="flex gap-2 flex-shrink-0">
        <Button
          icon="pi pi-download"
          :label="isMobile ? undefined : 'ייצוא CSV'"
          size="small"
          text
          rounded
          class="!rounded-xl !text-gray-400 hover:!text-gray-900"
          :disabled="!data"
          @click="exportCsv"
        />
        <Button
          icon="pi pi-refresh"
          :label="isMobile ? undefined : 'רענון'"
          size="small"
          :loading="loading"
          outlined
          rounded
          class="!rounded-xl"
          @click="fetchData"
        />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading && !data" class="flex justify-center items-center h-64">
      <ProgressSpinner style="width: 50px; height: 50px" />
    </div>

    <template v-if="data">
      <!-- KPI Cards -->
      <div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
        <KpiCard
          label="MRR"
          :value="formatCurrency(summary.mrr)"
          icon="pi-wallet"
          color="amber"
        />
        <KpiCard
          label="עסקים משלמים"
          :value="summary.payingBusinesses"
          icon="pi-verified"
          color="green"
        />
        <KpiCard
          label="עסקים פעילים"
          :value="summary.activeBusinesses"
          :previousValue="summary.prevActiveBusinesses"
          icon="pi-building"
          color="purple"
        />
        <KpiCard
          :label="`תורים (${selectedDays} ימים)`"
          :value="summary.totalAppointments"
          :previousValue="summary.prevTotalAppointments"
          icon="pi-calendar"
          color="blue"
        />
        <KpiCard
          label="תורים עתידיים"
          :value="summary.totalUpcomingAppointments"
          icon="pi-calendar-plus"
          color="teal"
        />
        <KpiCard
          label="לקוחות חדשים"
          :value="summary.totalNewCustomers"
          :previousValue="summary.prevTotalNewCustomers"
          icon="pi-user-plus"
          color="purple"
        />
        <KpiCard
          label="עסקים חדשים"
          :value="summary.newSignups"
          :previousValue="summary.prevNewSignups"
          icon="pi-plus-circle"
          color="red"
        />
        <KpiCard
          label="SMS (החודש)"
          :value="summary.totalSmsSent"
          icon="pi-envelope"
          color="orange"
        />
        <KpiCard
          label="WhatsApp (החודש)"
          :value="summary.totalWhatsappSent"
          icon="pi-whatsapp"
          color="teal"
        />
      </div>

      <!-- Trend chart -->
      <div v-if="data.perDay?.length" class="bg-white border border-gray-200 rounded-2xl p-4 md:p-5 mb-6 shadow-sm">
        <h2 class="text-sm font-semibold text-gray-900 mb-3">פעילות יומית בפלטפורמה ({{ selectedDays }} ימים, ללא עסקי בדיקה)</h2>
        <Chart type="bar" :data="trendChartData" :options="trendChartOptions" class="h-64" />
      </div>

      <!-- Action panels: trials ending soon -->
      <div v-if="trialsEndingSoon.length" class="mb-6">
        <div class="bg-white border border-amber-200 rounded-2xl p-4 shadow-sm">
          <div class="flex items-center gap-2 mb-3">
            <i class="pi pi-clock text-amber-500 text-sm" />
            <h2 class="text-sm font-semibold text-gray-900">ניסיון מסתיים בקרוב ({{ trialsEndingSoon.length }})</h2>
          </div>
          <div class="space-y-1.5 max-h-56 overflow-y-auto">
            <button
              v-for="b of trialsEndingSoon"
              :key="b.bid"
              class="w-full flex items-center justify-between gap-2 bg-amber-50/50 hover:bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 text-right transition-colors"
              @click="goToBusiness(b.bid)"
            >
              <div class="min-w-0">
                <div class="text-sm font-medium text-gray-900 truncate">{{ b.title }}</div>
                <div class="text-xs text-gray-400">{{ b.appointments14d }} תורים בתקופה</div>
              </div>
              <Tag
                :value="trialDaysLeftLabel(b)"
                :severity="trialDaysLeft(b) <= 2 ? 'danger' : 'warn'"
                class="!text-[10px] flex-shrink-0"
              />
            </button>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="bg-white border border-gray-200 rounded-2xl p-4 mb-4 shadow-sm">
        <div class="flex gap-3 items-center flex-wrap">
          <IconField class="w-full sm:w-auto">
            <InputIcon class="pi pi-search" />
            <InputText
              v-model="searchQuery"
              placeholder="חיפוש עסק..."
              class="!text-sm w-full"
            />
          </IconField>
          <Select
            v-model="selectedDays"
            :options="daysOptions"
            optionLabel="label"
            optionValue="value"
            class="!text-sm"
          />
          <Select
            v-model="filterPlan"
            :options="planOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="תוכנית"
            class="!text-sm"
            showClear
          />
          <Select
            v-model="filterStatus"
            :options="statusOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="סטטוס חוזה"
            class="!text-sm"
            showClear
          />
          <div class="flex items-center gap-2">
            <ToggleSwitch v-model="hideTestBusinesses" inputId="hideTest" />
            <label for="hideTest" class="text-xs text-gray-500 select-none cursor-pointer">
              הסתר עסקי בדיקה
            </label>
          </div>
        </div>
      </div>

      <!-- Business Table -->
      <div class="bg-white border border-gray-200 rounded-2xl overflow-x-auto shadow-sm">
        <DataTable
          :value="filteredBusinesses"
          paginator
          :rows="20"
          :rowsPerPageOptions="[10, 20, 50]"
          dataKey="bid"
          :sortField="'appointments14d'"
          :sortOrder="-1"
          stripedRows
          class="!text-sm"
          @row-click="onRowClick"
          rowHover
          :pt="{
            bodyRow: { class: 'cursor-pointer' }
          }"
        >
          <Column field="title" header="שם העסק" sortable style="min-width: 180px">
            <template #body="{ data }">
              <div class="flex flex-col">
                <span class="flex items-center gap-1.5">
                  <span class="font-medium text-gray-900">{{ data.title }}</span>
                  <Tag
                    v-if="data.isTestBusiness"
                    value="בדיקה"
                    severity="warn"
                    class="!text-[10px] !py-0 !px-1.5"
                  />
                </span>
                <span class="text-xs text-gray-400">{{ data.bid }}</span>
              </div>
            </template>
          </Column>
          <Column field="phone" header="טלפון" style="min-width: 120px" />
          <Column field="plan" header="תוכנית" sortable style="min-width: 100px">
            <template #body="{ data }">
              <Tag
                :value="data.plan === 'premium' ? 'פרימיום' : 'חינמי'"
                :severity="data.plan === 'premium' ? 'success' : 'secondary'"
              />
            </template>
          </Column>
          <Column field="contractStatus" header="סטטוס" sortable style="min-width: 100px">
            <template #body="{ data }">
              <Tag
                :value="contractStatusLabel(data.contractStatus)"
                :severity="contractStatusSeverity(data.contractStatus)"
              />
            </template>
          </Column>
          <Column field="appointments14d" :header="`תורים (${selectedDays}י)`" sortable style="min-width: 100px">
            <template #body="{ data }">
              <div class="flex items-center gap-1.5">
                <span class="font-semibold text-primary-600">{{ data.appointments14d }}</span>
                <i
                  v-if="data.prevAppointments > 0 && data.appointments14d < data.prevAppointments"
                  class="pi pi-arrow-down text-red-400 !text-[10px]"
                  :title="`${data.prevAppointments} בתקופה הקודמת`"
                />
                <i
                  v-else-if="data.appointments14d > data.prevAppointments"
                  class="pi pi-arrow-up text-emerald-400 !text-[10px]"
                  :title="`${data.prevAppointments} בתקופה הקודמת`"
                />
              </div>
            </template>
          </Column>
          <Column field="upcomingAppointments" header="עתידיים" sortable style="min-width: 90px">
            <template #body="{ data }">
              <span :class="data.upcomingAppointments > 0 ? 'font-medium text-teal-600' : 'text-gray-300'">
                {{ data.upcomingAppointments || 0 }}
              </span>
            </template>
          </Column>
          <Column field="newCustomers14d" header="לקוחות חדשים" sortable style="min-width: 110px" />
          <Column field="smsSent" header="SMS" sortable style="min-width: 80px" />
          <Column field="whatsappSent" header="WhatsApp" sortable style="min-width: 80px" />
          <Column field="lastAppointmentDate" header="תור אחרון" sortable style="min-width: 120px">
            <template #body="{ data }">
              <span v-if="data.lastAppointmentDate">{{ formatDate(data.lastAppointmentDate) }}</span>
              <span v-else class="text-gray-300">-</span>
            </template>
          </Column>
          <Column field="createdAt" header="נוצר" sortable style="min-width: 120px">
            <template #body="{ data }">
              {{ formatDate(data.createdAt) }}
            </template>
          </Column>
        </DataTable>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { DateTime } from 'luxon';
import type {
  IPlatformActivityResponse,
  IPlatformActivitySummary,
  IPlatformBusinessActivity,
} from '@torim-app/types';
import { ActivityApi } from '../api/activity-api';
import { PlatformRouteNames } from '../router/route-names';
import KpiCard from '../components/KpiCard.vue';
import { useIsMobile } from '../composables/useIsMobile';
import { useNotify } from '../composables/useNotify';
import { formatCurrency } from '../composables/useLeadDisplay';

const { isMobile } = useIsMobile();
const { notifyError } = useNotify();
const router = useRouter();
const loading = ref(false);
const data = ref<IPlatformActivityResponse | null>(null);
const searchQuery = ref('');
const filterPlan = ref<string | null>(null);
const filterStatus = ref<string | null>(null);
const hideTestBusinesses = ref(true);
const selectedDays = ref(30);

const daysOptions = [
  { label: '14 ימים אחרונים', value: 14 },
  { label: '30 ימים אחרונים', value: 30 },
  { label: '60 ימים אחרונים', value: 60 },
];

const planOptions = [
  { label: 'פרימיום', value: 'premium' },
  { label: 'חינמי', value: 'free' },
];

const statusOptions = [
  { label: 'ניסיון', value: 'trial' },
  { label: 'פעיל', value: 'active' },
  { label: 'פג תוקף', value: 'expired' },
];

const emptySummary: IPlatformActivitySummary = {
  activeBusinesses: 0,
  totalAppointments: 0,
  totalNewCustomers: 0,
  totalSmsSent: 0,
  totalWhatsappSent: 0,
  newSignups: 0,
  totalUpcomingAppointments: 0,
  mrr: 0,
  payingBusinesses: 0,
  prevActiveBusinesses: 0,
  prevTotalAppointments: 0,
  prevTotalNewCustomers: 0,
  prevNewSignups: 0,
};

const summary = computed<IPlatformActivitySummary>(() => {
  if (!data.value) return emptySummary;
  if (!hideTestBusinesses.value) return data.value.summary;

  const rows = data.value.businesses.filter((b) => !b.isTestBusiness);
  const active = rows.filter((b) => b.appointments14d > 0 || b.newCustomers14d > 0);
  const prevActive = rows.filter((b) => (b.prevAppointments || 0) > 0 || (b.prevNewCustomers || 0) > 0);
  const windowStart = Date.now() - selectedDays.value * 86400000;
  const prevWindowStart = Date.now() - selectedDays.value * 2 * 86400000;
  return {
    activeBusinesses: active.length,
    totalAppointments: rows.reduce((s, b) => s + (b.appointments14d || 0), 0),
    totalNewCustomers: rows.reduce((s, b) => s + (b.newCustomers14d || 0), 0),
    totalSmsSent: rows.reduce((s, b) => s + (b.smsSent || 0), 0),
    totalWhatsappSent: rows.reduce((s, b) => s + (b.whatsappSent || 0), 0),
    newSignups: rows.filter((b) => b.createdAt && new Date(b.createdAt).getTime() >= windowStart).length,
    totalUpcomingAppointments: rows.reduce((s, b) => s + (b.upcomingAppointments || 0), 0),
    // MRR already excludes test businesses server-side
    mrr: data.value.summary.mrr,
    payingBusinesses: data.value.summary.payingBusinesses,
    prevActiveBusinesses: prevActive.length,
    prevTotalAppointments: rows.reduce((s, b) => s + (b.prevAppointments || 0), 0),
    prevTotalNewCustomers: rows.reduce((s, b) => s + (b.prevNewCustomers || 0), 0),
    prevNewSignups: rows.filter((b) => {
      if (!b.createdAt) return false;
      const t = new Date(b.createdAt).getTime();
      return t >= prevWindowStart && t < windowStart;
    }).length,
  };
});

const nonTestBusinesses = computed(() => {
  if (!data.value) return [];
  return hideTestBusinesses.value
    ? data.value.businesses.filter((b) => !b.isTestBusiness)
    : data.value.businesses;
});

const filteredBusinesses = computed(() => {
  let list = nonTestBusinesses.value;

  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase();
    list = list.filter(
      (b) =>
        b.title?.toLowerCase().includes(q) ||
        b.bid?.toLowerCase().includes(q) ||
        b.phone?.includes(q)
    );
  }

  if (filterPlan.value) {
    list = list.filter((b) => b.plan === filterPlan.value);
  }

  if (filterStatus.value) {
    list = list.filter((b) => b.contractStatus === filterStatus.value);
  }

  return list;
});

const formatDate = (date: Date | string | undefined) => {
  if (!date) return '-';
  return DateTime.fromJSDate(new Date(date)).toFormat('dd/MM/yyyy');
};

const contractStatusLabel = (status: string) => {
  const map: Record<string, string> = {
    trial: 'ניסיון',
    active: 'פעיל',
    expired: 'פג תוקף',
  };
  return map[status] || status;
};

const contractStatusSeverity = (status: string) => {
  const map: Record<string, string> = {
    trial: 'warn',
    active: 'success',
    expired: 'danger',
  };
  return map[status] || 'secondary';
};

const onRowClick = (event: { data: IPlatformBusinessActivity }) => {
  router.push({ name: PlatformRouteNames.BusinessView, params: { bid: event.data.bid } });
};

const goToBusiness = (bid: string) => {
  router.push({ name: PlatformRouteNames.BusinessView, params: { bid } });
};

// --- Action panels ---
const trialDaysLeft = (b: IPlatformBusinessActivity): number => {
  if (!b.trialEndDate) return Infinity;
  return Math.ceil(DateTime.fromJSDate(new Date(b.trialEndDate)).diff(DateTime.local(), 'days').days);
};

const trialDaysLeftLabel = (b: IPlatformBusinessActivity): string => {
  const days = trialDaysLeft(b);
  if (days === 1) return 'יום אחד';
  return `${days} ימים`;
};

// Only trials still running — businesses whose trial already ended are excluded
const trialsEndingSoon = computed(() =>
  nonTestBusinesses.value
    .filter((b) => {
      if (b.contractStatus !== 'trial' || !b.trialEndDate) return false;
      const daysLeft = trialDaysLeft(b);
      return daysLeft > 0 && daysLeft <= 7;
    })
    .sort((a, b) => trialDaysLeft(a) - trialDaysLeft(b)),
);

// --- Trend chart ---
const trendChartData = computed(() => {
  const perDay = data.value?.perDay || [];
  return {
    labels: perDay.map((d) => DateTime.fromISO(d.date).toFormat('dd/MM')),
    datasets: [
      {
        type: 'bar',
        label: 'תורים',
        data: perDay.map((d) => d.appointments),
        backgroundColor: 'rgba(136, 100, 238, 0.6)',
        borderColor: '#8864ee',
        borderWidth: 1,
        borderRadius: 4,
        yAxisID: 'y',
      },
      {
        type: 'line',
        label: 'עסקים חדשים',
        data: perDay.map((d) => d.signups),
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.15)',
        tension: 0.3,
        pointRadius: 2,
        yAxisID: 'y1',
      },
    ],
  };
});

const trendChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom', labels: { font: { size: 11 }, color: '#6b7280' } },
  },
  scales: {
    y: {
      beginAtZero: true,
      position: 'right',
      ticks: { color: '#9ca3af', precision: 0 },
      grid: { color: 'rgba(0, 0, 0, 0.05)' },
    },
    y1: {
      beginAtZero: true,
      position: 'left',
      ticks: { color: '#10b981', precision: 0 },
      grid: { display: false },
    },
    x: {
      ticks: { maxTicksLimit: 15, font: { size: 10 }, color: '#9ca3af' },
      grid: { display: false },
    },
  },
};

// --- CSV export ---
const exportCsv = () => {
  const rows = filteredBusinesses.value;
  const header = [
    'שם העסק', 'BID', 'טלפון', 'תוכנית', 'סטטוס', `תורים (${selectedDays.value} ימים)`,
    'תורים תקופה קודמת', 'תורים עתידיים', 'לקוחות חדשים', 'SMS', 'WhatsApp', 'תור אחרון', 'נוצר',
  ];
  const escape = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [
    header.map(escape).join(','),
    ...rows.map((b) =>
      [
        b.title, b.bid, b.phone, b.plan, b.contractStatus, b.appointments14d,
        b.prevAppointments || 0, b.upcomingAppointments || 0, b.newCustomers14d,
        b.smsSent, b.whatsappSent,
        b.lastAppointmentDate ? formatDate(b.lastAppointmentDate) : '',
        b.createdAt ? formatDate(b.createdAt) : '',
      ].map(escape).join(','),
    ),
  ];
  // BOM so Excel opens Hebrew UTF-8 correctly
  const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `platform-activity-${DateTime.local().toFormat('yyyy-MM-dd')}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
};

const fetchData = async () => {
  loading.value = true;
  try {
    data.value = await ActivityApi.fetchActivityDashboard(selectedDays.value);
  } catch (e) {
    console.error('Failed to fetch activity data:', e);
    notifyError('טעינת הנתונים נכשלה', 'נסה לרענן את העמוד');
  } finally {
    loading.value = false;
  }
};

watch(selectedDays, () => fetchData());

onMounted(fetchData);
</script>
